import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiPost, getApiError } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { Money } from '../../components/Money';
import { cn } from '../../lib/cn';

interface ReturnableLine {
  id: string;
  product: { sku: string; name: string; category?: { fulfillmentStrategy: string } };
  qty: number;
  returnedQty: number;
  unitPrice: string;
  selectedColor: string | null;
  selectedSideColor: string | null;
  selectedFabric: string | null;
}

const RETURN_WINDOW_DAYS = 4;
const isSpecialOrder = (l: ReturnableLine): boolean =>
  (l.product.category?.fulfillmentStrategy ?? '').startsWith('MADE_TO_ORDER');

interface CreditNoteResult {
  id: string;
  number: string;
  total: string;
}

export function ReturnModal({
  orderId,
  orderNumber,
  customerId,
  receiptDate,
  lines,
  onClose,
}: {
  orderId: string;
  orderNumber: string;
  customerId: string;
  receiptDate: string;
  lines: ReturnableLine[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState(false);
  const [done, setDone] = useState<CreditNoteResult | null>(null);

  const daysSinceReceipt = Math.floor((Date.now() - new Date(receiptDate).getTime()) / 86_400_000);
  const windowExpired = daysSinceReceipt > RETURN_WINDOW_DAYS;

  // A line is blocked by policy (special order, or past the window) unless the
  // owner overrides.
  const blocked = (l: ReturnableLine): boolean => !override && (windowExpired || isSpecialOrder(l));

  const setQty = (lineId: string, max: number, v: number): void =>
    setQtys((q) => ({ ...q, [lineId]: Math.max(0, Math.min(max, v)) }));

  const payloadLines = lines
    .filter((l) => !blocked(l))
    .map((l) => ({ orderLineId: l.id, qty: qtys[l.id] ?? 0 }))
    .filter((l) => l.qty > 0);
  const refundEstimate = lines
    .filter((l) => !blocked(l))
    .reduce((s, l) => s + (qtys[l.id] ?? 0) * Number(l.unitPrice), 0);
  const canSubmit = payloadLines.length > 0 && (!override || reason.trim().length > 0);

  const process = useMutation({
    mutationFn: () =>
      apiPost<CreditNoteResult>(`/orders/${orderId}/return`, {
        lines: payloadLines,
        reason: reason.trim() || undefined,
        overridePolicy: override,
      }),
  });

  const runReturn = async (thenExchange: boolean): Promise<void> => {
    const cn = await process.mutateAsync();
    qc.invalidateQueries({ queryKey: ['order', orderId] });
    if (thenExchange) {
      navigate('/orders/new', {
        state: {
          exchange: {
            customerId,
            creditNoteId: cn.id,
            creditNumber: cn.number,
            creditAmount: cn.total,
            fromNumber: orderNumber,
          },
        },
      });
    } else {
      setDone(cn);
    }
  };

  if (done) {
    return (
      <Modal
        title="Return processed"
        size="md"
        onClose={onClose}
        footer={<Button onClick={onClose}>Done</Button>}
      >
        <div className="space-y-3 text-sm">
          <p className="text-ink">
            Credit note <span className="kc-mono font-medium">{done.number}</span> issued for{' '}
            <Money value={done.total} className="font-medium" />.
          </p>
          <p className="text-ink-3">
            Stocked items were returned to inventory. The refund credit can be applied to a future
            invoice or paid out.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Return / exchange · ${orderNumber}`}
      size="2xl"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            loading={process.isPending}
            disabled={!canSubmit}
            onClick={() => void runReturn(true)}
            title="Process the return, then start a replacement order with the credit applied"
          >
            Return &amp; exchange →
          </Button>
          <Button
            loading={process.isPending}
            disabled={!canSubmit}
            onClick={() => void runReturn(false)}
          >
            Process return
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-3">
          Choose how many of each item the customer is returning. Stocked items go back into
          inventory; a refund credit note is issued for the value.
        </p>

        {/* Policy banner — Kalachand T&C */}
        <div
          className={cn(
            'rounded-md border px-3 py-2 text-xs',
            windowExpired && !override
              ? 'border-negative/40 bg-negative/5 text-negative'
              : 'border-rule bg-paper-2 text-ink-3',
          )}
        >
          {windowExpired ? (
            <>
              Received <b>{daysSinceReceipt} days ago</b> — past the {RETURN_WINDOW_DAYS}-day claim
              window.{' '}
            </>
          ) : (
            <>
              Within the {RETURN_WINDOW_DAYS}-day claim window (received {daysSinceReceipt} day
              {daysSinceReceipt === 1 ? '' : 's'} ago).{' '}
            </>
          )}
          Special (made-to-order) items are non-refundable.
        </div>
        <div className="border border-rule rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-ink-3 bg-paper-2 border-b border-rule">
                <th className="px-3 py-2 font-medium">Item</th>
                <th className="px-3 py-2 font-medium text-right">Bought</th>
                <th className="px-3 py-2 font-medium text-right">Already returned</th>
                <th className="px-3 py-2 font-medium text-right">Return now</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const returnable = l.qty - l.returnedQty;
                const finish = [l.selectedColor, l.selectedSideColor, l.selectedFabric]
                  .filter(Boolean)
                  .join(' · ');
                const special = isSpecialOrder(l);
                const isBlocked = blocked(l) || returnable <= 0;
                const reasonLabel =
                  returnable <= 0
                    ? 'fully returned'
                    : special && !override
                      ? 'special order · non-refundable'
                      : windowExpired && !override
                        ? 'window passed'
                        : null;
                return (
                  <tr key={l.id} className="border-b border-rule-2 last:border-0">
                    <td className="px-3 py-2">
                      <div className={cn('text-ink', isBlocked && 'text-ink-3')}>
                        {l.product.name}
                      </div>
                      <div className="text-[11px] text-ink-3 kc-mono">
                        {l.product.sku}
                        {finish ? ` · ${finish}` : ''}
                        {special ? ' · special order' : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right kc-mono">{l.qty}</td>
                    <td className="px-3 py-2 text-right kc-mono text-ink-3">
                      {l.returnedQty || '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        min={0}
                        max={returnable}
                        disabled={isBlocked}
                        value={isBlocked ? 0 : (qtys[l.id] ?? 0)}
                        onChange={(e) => setQty(l.id, returnable, Number(e.target.value))}
                        className={cn(
                          'kc-input kc-input-sm w-20 text-right',
                          isBlocked && 'opacity-40',
                        )}
                      />
                      {reasonLabel && (
                        <div className="text-[10px] text-ink-4 mt-0.5">{reasonLabel}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
            Reason {override ? '(required for override)' : '(optional)'}
          </span>
          <input
            className="kc-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. wrong size, faulty corner"
          />
        </label>

        {/* Owner override — bypasses the window + special-order guards */}
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={override}
            onChange={(e) => setOverride(e.target.checked)}
            className="mt-0.5"
          />
          <span className="text-ink-2">
            Override return policy{' '}
            <span className="text-ink-3">
              — allow special orders and/or returns past the {RETURN_WINDOW_DAYS}-day window (e.g. a
              defective item). The override is recorded on the credit note.
            </span>
          </span>
        </label>

        {canSubmit && (
          <div className="flex items-baseline justify-between text-sm border-t border-rule pt-3">
            <span className="text-ink-3">Estimated refund credit</span>
            <Money value={String(refundEstimate)} className="text-lg font-medium text-ink" />
          </div>
        )}
        {process.isError && (
          <div className="text-xs text-negative">{getApiError(process.error).message}</div>
        )}
      </div>
    </Modal>
  );
}
